import { lazy, ReactNode, Suspense } from 'react'

import EyeIcon from '@heroicons/react/outline/esm/EyeIcon.js'
import DownloadIcon from '@heroicons/react/solid/esm/DownloadIcon.js'
import { clsx } from 'clsx'

import { fileSize } from '@bettermode/common/filesize'
import { Media } from '@tribeplatform/react-ui-kit/Media'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/index.js'

const FileTypeIcon = lazy(() =>
  import('../../Composer/components/Attachment/FileTypeIcon.js').then(m => ({
    default: m.FileTypeIcon,
  })),
)

export type FileListItemProps = {
  name: string
  extension?: string
  size?: number
  downloadUrl?: string
  onPreview?: (e) => void
  action?: ReactNode
  className?: string
}

export const FileListItem = ({
  name,
  extension,
  size,
  downloadUrl,
  action,
  className,
  onPreview,
  ...rest
}: FileListItemProps) => {
  const { $t } = useI18n()

  const trailingVisual = action || (
    <div className="flex items-center space-s-3">
      {onPreview && (
        <Tooltip>
          <Tooltip.Trigger>
            <EyeIcon
              onClick={onPreview}
              className="cursor-pointer x-6 h-6 text-content-subdued hover:text-content-hovered"
            />
          </Tooltip.Trigger>
          <Tooltip.Panel>
            <T id="Generics.Preview.Verb" defaultMessage="Preview" />
          </Tooltip.Panel>
        </Tooltip>
      )}
      {downloadUrl && (
        <Tooltip>
          <Tooltip.Trigger>
            <a
              href={downloadUrl}
              download={name}
              onClick={e => {
                e.stopPropagation()
              }}
              aria-label={$t({
                defaultMessage: 'Download',
                id: 'Generics.Download',
              })}
            >
              <DownloadIcon className="cursor-pointer x-6 h-6 text-content-subdued hover:text-content-hovered" />
            </a>
          </Tooltip.Trigger>
          <Tooltip.Panel>
            <T id="Generics.Download" defaultMessage="Download" />
          </Tooltip.Panel>
        </Tooltip>
      )}
    </div>
  )

  const onClick = e => {
    if (typeof onPreview === 'function') {
      onPreview(e)
    }
  }

  return (
    <Media
      className={clsx(
        'flex-1 px-6 py-4 rounded-base border border-line-subdued min-w-0',
        onPreview && 'cursor-pointer',
        className,
      )}
      align="center"
      icon={
        <Suspense fallback={<></>}>
          <FileTypeIcon
            extension={extension}
            className="w-9 h-9 text-content-subdued"
          />
        </Suspense>
      }
      trailingVisual={trailingVisual}
      onClick={onClick}
      {...rest}
    >
      <div className="h-12 flex flex-col justify-center">
        <div className="text-content truncate" title={name}>
          {name}
        </div>
        {size && (
          <div className="text-content-subdued truncate" title={fileSize(size)}>
            {fileSize(size)}
          </div>
        )}
      </div>
    </Media>
  )
}
